import { useEffect, useRef } from 'react'
import { useThree } from '@react-three/fiber'
import { getAsset } from '../assets/catalog'
import { useDesign } from '../state/designStore'
import { useEditor } from '../state/editorStore'
import type { RotationSteps } from '../state/document'
import { panBasis, clampHalfY } from './camera'
import { hitTargets, uidForObject } from './itemRegistry'
import { groundPoint, pickItem, resolveDrop } from './placement'

/** Pointer travel below this is a click, not a drag. */
const CLICK_SLOP = 3

type Gesture =
  | { kind: 'pan'; lastX: number; lastY: number; travel: number; pendingPlace: boolean; pendingSelect: string | null }
  | { kind: 'drag'; uid: string; grabX: number; grabZ: number; rotation: RotationSteps; travel: number }

/**
 * All pointer handling for the editor, bound natively to the canvas.
 *
 * R3F's mesh events are not used: a single `stopPropagation()` anywhere upstream
 * would silently kill the ground handler mid-gesture, and native listeners give
 * us pointer capture and a stable target through the whole drag.
 */
export function Interaction() {
  const gl = useThree((state) => state.gl)
  const camera = useThree((state) => state.camera)
  const invalidate = useThree((state) => state.invalidate)
  const gesture = useRef<Gesture | null>(null)

  useEffect(() => {
    const canvas = gl.domElement

    const currentRoom = () => useDesign.getState().doc.room
    const currentItems = () => useDesign.getState().doc.items

    const updateGhost = (event: PointerEvent) => {
      const { tool, setGhost } = useEditor.getState()
      if (tool.kind !== 'place') return
      const asset = getAsset(tool.assetId)
      const point = groundPoint(event, canvas, camera)
      if (!asset || !point) {
        setGhost(null)
        return
      }
      const drop = resolveDrop({
        asset,
        x: point.x,
        z: point.z,
        rotation: tool.rotation,
        room: currentRoom(),
        items: currentItems(),
      })
      setGhost({ x: drop.x, z: drop.z, y: drop.y, rotation: drop.rotation, wall: drop.wall, valid: drop.valid })
      invalidate()
    }

    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 0 && event.button !== 1 && event.button !== 2) return
      try {
        canvas.setPointerCapture(event.pointerId)
      } catch {
        // Synthetic pointers (tests, automation) have no active pointer id.
      }
      const editor = useEditor.getState()

      // Middle/right button always pans, whatever the active tool.
      const forcePan = event.button !== 0

      if (!forcePan && editor.tool.kind === 'place') {
        gesture.current = {
          kind: 'pan',
          lastX: event.clientX,
          lastY: event.clientY,
          travel: 0,
          pendingPlace: true,
          pendingSelect: null,
        }
        return
      }

      const hit = forcePan ? null : pickItem(event, canvas, camera, hitTargets())
      const uid = hit ? uidForObject(hit.object) : null

      if (uid) {
        const item = currentItems().find((entry) => entry.uid === uid)
        const point = groundPoint(event, canvas, camera, item?.y ?? 0)
        gesture.current = {
          kind: 'drag',
          uid,
          grabX: point ? point.x - (item?.x ?? 0) : 0,
          grabZ: point ? point.z - (item?.z ?? 0) : 0,
          rotation: item?.rotation ?? 0,
          travel: 0,
        }
        editor.select(uid)
        editor.setDragging(true)
        invalidate()
        return
      }

      gesture.current = {
        kind: 'pan',
        lastX: event.clientX,
        lastY: event.clientY,
        travel: 0,
        pendingPlace: false,
        pendingSelect: null,
      }
    }

    const onPointerMove = (event: PointerEvent) => {
      const active = gesture.current
      const editor = useEditor.getState()

      if (!active) {
        if (editor.tool.kind === 'place') {
          updateGhost(event)
        } else {
          const hit = pickItem(event, canvas, camera, hitTargets())
          const uid = hit ? uidForObject(hit.object) : null
          if (uid !== editor.hoveredId) {
            editor.hover(uid)
            invalidate()
          }
        }
        return
      }

      if (active.kind === 'pan') {
        const dx = event.clientX - active.lastX
        const dy = event.clientY - active.lastY
        active.travel += Math.abs(dx) + Math.abs(dy)
        active.lastX = event.clientX
        active.lastY = event.clientY
        if (active.travel > CLICK_SLOP) {
          const { view } = editor
          const basis = panBasis(view.mode, view.viewIndex)
          const worldPerPixel = (2 * view.halfY) / canvas.clientHeight
          const moveX = -basis.right.x * dx * worldPerPixel + basis.up.x * dy * worldPerPixel * basis.upScale
          const moveZ = -basis.right.z * dx * worldPerPixel + basis.up.z * dy * worldPerPixel * basis.upScale
          editor.panBy(moveX, moveZ)
          invalidate()
        }
        return
      }

      active.travel += 1
      const item = currentItems().find((entry) => entry.uid === active.uid)
      const asset = item ? getAsset(item.assetId) : undefined
      if (!item || !asset) return
      const point = groundPoint(event, canvas, camera, item.y)
      if (!point) return

      const drop = resolveDrop({
        asset,
        x: point.x - active.grabX,
        z: point.z - active.grabZ,
        rotation: active.rotation,
        room: currentRoom(),
        items: currentItems(),
        excludeUid: active.uid,
        mountHeight: item.y,
      })
      editor.setDragPose({
        uid: active.uid,
        x: drop.x,
        z: drop.z,
        y: drop.y,
        rotation: drop.rotation,
        wall: drop.wall,
        valid: drop.valid,
      })
      invalidate()
    }

    const finishGesture = (event: PointerEvent) => {
      const active = gesture.current
      gesture.current = null
      try {
        if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId)
      } catch {
        // Same as above: nothing to release for a synthetic pointer.
      }
      const editor = useEditor.getState()
      const design = useDesign.getState()

      if (!active) return

      if (active.kind === 'drag') {
        const pose = editor.dragPose
        editor.setDragPose(null)
        editor.setDragging(false)
        // One document write per gesture => exactly one undo entry.
        if (pose && active.travel > 0) {
          design.updateItem(active.uid, {
            x: pose.x,
            z: pose.z,
            y: pose.y,
            rotation: pose.rotation,
            wall: pose.wall,
          })
        }
        invalidate()
        return
      }

      if (active.travel > CLICK_SLOP) return

      if (active.pendingPlace && editor.tool.kind === 'place') {
        const ghost = editor.ghost
        const tool = editor.tool
        if (ghost && (ghost.valid || event.altKey)) {
          const uid = design.addItem({
            assetId: tool.assetId,
            x: ghost.x,
            z: ghost.z,
            y: ghost.y,
            rotation: ghost.rotation,
            variant: tool.variant,
            wall: ghost.wall,
          })
          // Shift keeps the tool armed for repeat placement, like every planner.
          if (!event.shiftKey) {
            editor.cancelPlacement()
            editor.select(uid)
          }
        }
        invalidate()
        return
      }

      editor.select(null)
      invalidate()
    }

    const onWheel = (event: WheelEvent) => {
      event.preventDefault()
      const editor = useEditor.getState()
      const { view } = editor
      const factor = Math.exp(event.deltaY * 0.0012)
      const nextHalfY = clampHalfY(view.halfY * factor)
      const applied = nextHalfY / view.halfY

      // Anchor the zoom on the cursor: keep the world point under it fixed.
      const point = groundPoint(event, canvas, camera)
      if (point) {
        editor.setView({
          halfY: nextHalfY,
          cx: point.x + applied * (view.cx - point.x),
          cy: applied * view.cy,
          cz: point.z + applied * (view.cz - point.z),
        })
      } else {
        editor.setView({ halfY: nextHalfY })
      }
      invalidate()
    }

    const onContextMenu = (event: MouseEvent) => {
      event.preventDefault()
      const editor = useEditor.getState()
      if (editor.tool.kind === 'place') editor.cancelPlacement()
    }

    const onPointerLeave = () => {
      const editor = useEditor.getState()
      if (editor.hoveredId) editor.hover(null)
      if (!gesture.current && editor.tool.kind === 'place') editor.setGhost(null)
      invalidate()
    }

    canvas.addEventListener('pointerdown', onPointerDown)
    canvas.addEventListener('pointermove', onPointerMove)
    canvas.addEventListener('pointerup', finishGesture)
    canvas.addEventListener('pointercancel', finishGesture)
    canvas.addEventListener('pointerleave', onPointerLeave)
    canvas.addEventListener('wheel', onWheel, { passive: false })
    canvas.addEventListener('contextmenu', onContextMenu)

    return () => {
      canvas.removeEventListener('pointerdown', onPointerDown)
      canvas.removeEventListener('pointermove', onPointerMove)
      canvas.removeEventListener('pointerup', finishGesture)
      canvas.removeEventListener('pointercancel', finishGesture)
      canvas.removeEventListener('pointerleave', onPointerLeave)
      canvas.removeEventListener('wheel', onWheel)
      canvas.removeEventListener('contextmenu', onContextMenu)
    }
  }, [gl, camera, invalidate])

  return null
}

/** Keyboard shortcuts. Lives outside the canvas so it also works over the panels. */
export function useKeyboardShortcuts() {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return
      if (target?.isContentEditable) return

      const editor = useEditor.getState()
      const design = useDesign.getState()
      const meta = event.metaKey || event.ctrlKey

      if (meta && event.key.toLowerCase() === 'z') {
        event.preventDefault()
        const temporal = useDesign.temporal.getState()
        if (event.shiftKey) temporal.redo()
        else temporal.undo()
        return
      }
      if (meta && event.key.toLowerCase() === 'y') {
        event.preventDefault()
        useDesign.temporal.getState().redo()
        return
      }
      if (meta && event.key.toLowerCase() === 'd') {
        event.preventDefault()
        if (editor.selectedId) {
          const uid = design.duplicateItem(editor.selectedId)
          if (uid) editor.select(uid)
        }
        return
      }

      switch (event.key) {
        case 'Escape':
          if (editor.tool.kind === 'place') editor.cancelPlacement()
          else editor.select(null)
          break
        case 'Delete':
        case 'Backspace':
          // Backspace navigates back in some browsers unless we stop it.
          event.preventDefault()
          if (editor.selectedId) {
            design.removeItem(editor.selectedId)
            editor.select(null)
          }
          break
        case 'r':
        case 'R':
          if (editor.tool.kind === 'place') {
            editor.rotateTool(event.shiftKey ? -1 : 1)
          } else if (editor.selectedId) {
            const item = design.doc.items.find((entry) => entry.uid === editor.selectedId)
            if (item) {
              const next = ((((item.rotation + (event.shiftKey ? -1 : 1)) % 4) + 4) % 4) as RotationSteps
              design.updateItem(item.uid, { rotation: next })
            }
          }
          break
        case 'q':
        case 'Q':
          editor.rotateView(-1)
          break
        case 'e':
        case 'E':
          editor.rotateView(1)
          break
        case 'f':
        case 'F':
          editor.requestFit()
          break
        default:
          break
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])
}
